import { useEffect, useRef, useState, type CSSProperties, type RefObject } from "react";
import { createPortal } from "react-dom";

import { readPref } from "../prefs";
import palette from "./effects.json";
import { emptyScene, landing, step, type Scene, type Stage } from "./effects";
import type { Tier } from "./reel";
import { boom } from "./sound";
import classes from "./case.module.css";

/** One frame of the scene on the canvas: rings, then sparks, coins and smoke, then the flash. The
 * achievements' unlock card draws its fireworks with it too. */
export function draw(context: CanvasRenderingContext2D, scene: Scene, width: number, height: number): void {
  context.clearRect(0, 0, width, height);
  context.globalCompositeOperation = "lighter";
  for (const ring of scene.rings) {
    const t = ring.life / ring.max;
    context.globalAlpha = Math.max(0, 1 - t);
    context.strokeStyle = ring.color;
    context.lineWidth = ring.width * (1 - t) + 0.5;
    context.beginPath();
    context.arc(ring.x, ring.y, ring.radius * (1 - (1 - t) ** 3), 0, Math.PI * 2);
    context.stroke();
  }
  for (const particle of scene.particles) {
    const t = particle.life / particle.max;
    context.globalAlpha = Math.max(0, 1 - t);
    context.fillStyle = particle.color;
    context.beginPath();
    if (particle.kind === "coin") {
      context.globalCompositeOperation = "source-over";
      context.ellipse(particle.x, particle.y, particle.size * Math.abs(Math.cos(particle.spin)) + 1, particle.size, 0, 0, Math.PI * 2);
      context.fill();
      context.strokeStyle = palette.coinEdge;
      context.lineWidth = 1;
      context.stroke();
      context.globalCompositeOperation = "lighter";
    } else if (particle.kind === "smoke") {
      context.globalCompositeOperation = "source-over";
      context.globalAlpha = Math.max(0, 0.35 * (1 - t));
      context.arc(particle.x, particle.y, particle.size * (1 + t * 3), 0, Math.PI * 2);
      context.fill();
      context.globalCompositeOperation = "lighter";
    } else {
      context.arc(particle.x, particle.y, particle.size * (1 - t * 0.6), 0, Math.PI * 2);
      context.fill();
    }
  }
  context.globalAlpha = 1;
  context.globalCompositeOperation = "source-over";
  if (scene.flash !== null) {
    context.fillStyle = scene.flash.color;
    context.globalAlpha = Math.max(0, scene.flash.alpha * (1 - scene.flash.life / scene.flash.max));
    context.fillRect(0, 0, width, height);
    context.globalAlpha = 1;
  }
}

/** A shake of `element`, `amplitude` pixels for `ms`, through the Web Animations API. */
function shake(element: HTMLElement | null, amplitude: number, ms: number): void {
  if (element === null || typeof element.animate !== "function" || amplitude <= 0) return;
  const frames = Array.from({ length: 10 }, () => ({
    transform: `translate(${(Math.random() - 0.5) * 2 * amplitude}px, ${(Math.random() - 0.5) * 2 * amplitude}px)`,
  }));
  element.animate([...frames, { transform: "translate(0, 0)" }], { duration: ms, easing: "ease-out" });
}

export interface Landing {
  tier: Tier;
  /** Where the winning card sits, in the viewport's CSS pixels. */
  at: DOMRect;
}

/**
 * The landing's celebration over the whole page (spec web/case-opening): each rarity its beats,
 * Mythic's explosions and the jackpot's fireworks and coins, drawn on one canvas that the pointer
 * passes through, a shake of `shaken`, the booms when the case's sound is on, and the tier's shout.
 * Reduced motion has none of it: the reveal says what was won.
 */
export function Celebration({
  landing: landed,
  shaken,
}: {
  landing: Landing;
  shaken: RefObject<HTMLElement | null>;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [shout, setShout] = useState<{ text: string; color: string } | null>(null);
  useEffect(() => {
    const element = canvas.current;
    if (element === null) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    const width = window.innerWidth;
    const height = window.innerHeight;
    element.width = width * ratio;
    element.height = height * ratio;
    const context = element.getContext("2d");
    context?.setTransform(ratio, 0, 0, ratio, 0, 0);
    const scene = emptyScene();
    const color = getComputedStyle(document.documentElement).getPropertyValue(`--cai-rarity-${landed.tier}`).trim() || palette.white;
    const stage: Stage = { x: landed.at.left + landed.at.width / 2, y: landed.at.top + landed.at.height / 2, width, height };
    let frame = 0;
    const loop = () => {
      const alive = step(scene, height);
      if (context !== null) draw(context, scene, width, height);
      frame = alive ? requestAnimationFrame(loop) : 0;
    };
    const timers = landing(landed.tier, color, stage, Math.random).map((beat) =>
      window.setTimeout(() => {
        beat.run(scene);
        if (beat.shake !== undefined) shake(shaken.current, beat.shake.amplitude, beat.shake.ms);
        if (beat.boom !== undefined && readPref("caseSound")) boom(beat.boom);
        if (beat.shout !== undefined) setShout(beat.shout);
        if (frame === 0) frame = requestAnimationFrame(loop);
      }, beat.at),
    );
    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      cancelAnimationFrame(frame);
    };
  }, [landed, shaken]);
  // On the body, in this same commit: the dialog's transform would make it the frame of anything
  // fixed inside it, and Mantine's Portal mounts a render later, after this effect has looked.
  return createPortal(
    <>
      <canvas className={classes.effects} ref={canvas} aria-hidden />
      {shout === null ? null : (
        <span className={classes.shout} style={{ "--glow": shout.color } as CSSProperties} aria-hidden>
          {shout.text}
        </span>
      )}
    </>,
    document.body,
  );
}
